<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';
import type { ProductModelLookupItem } from '@/helpers/fetchers';

import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import { fetchGetLookupProductModel } from '@/helpers/fetchers';

import fieldStyles from '@/styles/field.module.scss';

export interface Props {
  modelValue?: string | null;
  disabled?: boolean;
  readonly?: boolean;
  label?: string;
  size?: FieldSize;
  rules?: ((v: string | null) => boolean | string)[];
  modelNotFound?: boolean;
  fetchItems?: (search: string) => Promise<ProductModelLookupItem[]>;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  disabled: false,
  readonly: false,
  label: 'Model',
  size: 'md',
  rules: () => [],
  modelNotFound: false,
  fetchItems: fetchGetLookupProductModel,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'select-item': [item: ProductModelLookupItem];
}>();

const fieldValue = ref<string | null>(props.modelValue ?? null);
const search = ref('');
const items = ref<ProductModelLookupItem[]>([]);
const isLoading = ref(false);

watch(
  () => props.modelValue,
  (newValue) => {
    fieldValue.value = newValue ?? null;
  },
);

watch(fieldValue, (newValue) => {
  emit('update:modelValue', newValue ?? '');
  if (newValue) {
    const item = items.value.find((i) => i.name === newValue);
    if (item) emit('select-item', item);
  }
});

const minSearchLength = 3;

function fetchWrapper(): void {
  const searchPattern = search.value;
  isLoading.value = true;
  void props
    .fetchItems(searchPattern)
    .then((fetchedItems) => {
      if (searchPattern !== search.value) return;
      items.value = fetchedItems;
    })
    .catch(() => {
      items.value = [];
    })
    .finally(() => {
      isLoading.value = false;
    });
}

const debounceFetchWrapper = useDebounceFn(fetchWrapper, 150);

function getSearchString(value?: string | null): string {
  return (value?.trim() || '').toUpperCase();
}

watch(search, (newSearch?: string | null, oldSearch?: string | null) => {
  if (props.readonly) return;
  newSearch = getSearchString(newSearch);
  oldSearch = getSearchString(oldSearch);

  if (newSearch.length >= minSearchLength) {
    if (newSearch !== oldSearch) {
      void debounceFetchWrapper();
    }
  } else {
    items.value = [];
  }
});

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-autocomplete
    v-model="fieldValue"
    v-model:search="search"
    :items="items"
    item-title="name"
    item-value="name"
    :loading="isLoading"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :label="props.label"
    :rules="props.rules"
    :class="[
      'g-v-text-field',
      $style.productModelAutocomplete,
      fieldStyles[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :clearable="!props.readonly"
    :menu-icon="props.readonly ? '' : 'mdi-chevron-down'"
    :menu-props="{ width: '360px', contentClass: $style.modelMenu }"
    auto-select-first
    data-testid="productModelAutocomplete"
  >
    <template #no-data>
      <NoDataSelectVListItem />
    </template>
    <template v-if="props.modelNotFound" #append-inner>
      <v-tooltip location="bottom">
        <template #activator="{ props: tooltipProps }">
          <v-icon v-bind="tooltipProps" :class="$style.warningIcon" size="16">
            mdi-alert-circle
          </v-icon>
        </template>
        No matching model found in the system
      </v-tooltip>
    </template>
  </v-autocomplete>
</template>

<style module lang="scss">
@use '@/styles/colors';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.productModelAutocomplete {
  :global {
    .v-field__input {
      padding-top: 0 !important;
    }
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-autocomplete__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.warningIcon {
  color: colors.$color-yellow-500;
  cursor: default;
}

.modelMenu {
  :global {
    .v-list-item-title {
      white-space: normal;
      line-height: 1.2;
      overflow: visible;
    }
  }
}
</style>
